'use client'

import { isValid } from 'date-fns'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { createElement, useEffect, useState } from 'react'
import { BiCalendar, BiLinkAlt, BiRightArrowAlt } from 'react-icons/bi'

import { Button } from '../Button'
import { ResumeButton } from './components/ResumeButton'
import { Stepper } from './components/Stepper'

import { HEADINGS } from './data/headings'
import { STEPS } from './data/steps'

import { Order } from '@/@types'
import { useCakeMakerActions, useCakeMakerState } from '@/store/useCakeMaker'
import { ConfirmationModal } from './components/ConfirmationModal'
import { ResumeModal } from './components/ResumeModal'

import './styles.scss'

type CakeMakerProps = {
  orders: Order[]
}

export function CakeMaker({ orders }: CakeMakerProps) {
  const { step, orderDate, batter, weight, shape, fillings, delivery } =
    useCakeMakerState()
  const { goToNextStep, goToPreviousStep, goToStep } = useCakeMakerActions()
  const [direction, setDirection] = useState<'next' | 'prev'>('next')
  const [prevStep, setPrevStep] = useState(step)
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [showBlockingMessage, setShowBlockingMessage] = useState(false)

  // A preferência só é legível no cliente. Aplicá-la já na primeira renderização
  // divergiria do HTML do servidor e quebraria a hidratação, então ela só entra
  // depois da montagem — antes de qualquer troca de etapa acontecer.
  const shouldReduceMotion = useReducedMotion()
  const [isMounted, setIsMounted] = useState(false)
  const prefersReducedMotion = isMounted && shouldReduceMotion

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const resetScroll = () => {
    const section = document.querySelector('section#cake-maker')
    if (!section) return

    setTimeout(() => {
      requestAnimationFrame(() => {
        section.scrollIntoView({
          inline: 'start',
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start',
        })
      })
    }, 50)
  }

  const listMissing = (items: string[]) => {
    if (items.length === 1) return items[0]
    return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`
  }

  /** Retorna o que ainda falta na etapa, ou null quando ela está completa. */
  const getBlockingMessage = () => {
    if (step === 1 && !isValid(orderDate)) {
      return 'Escolha uma data disponível no calendário para continuar.'
    }

    if (step === 2) {
      const missing = [
        !batter && 'a massa',
        !weight && 'o peso',
        !shape && 'o formato',
      ].filter(Boolean) as string[]

      if (missing.length > 0) {
        return `Escolha ${listMissing(missing)} para continuar.`
      }
    }

    if (step === 3 && fillings.length === 0) {
      return 'Escolha pelo menos um recheio para continuar.'
    }

    if (
      step === 5 &&
      delivery &&
      delivery.zipCode.length > 0 &&
      delivery.number?.length === 0
    ) {
      return 'Informe o número do endereço para concluir o agendamento.'
    }

    return null
  }

  const blockingMessage = getBlockingMessage()

  useEffect(() => {
    if (step !== prevStep) {
      resetScroll()
      setDirection(step > prevStep ? 'next' : 'prev')
      setPrevStep(step)
      setShowBlockingMessage(false)
    }
  }, [step, prevStep])

  useEffect(() => {
    if (!blockingMessage) setShowBlockingMessage(false)
  }, [blockingMessage])

  const slide = prefersReducedMotion ? 0 : 20

  const variants = {
    enter: (direction: string) => ({
      x: direction === 'next' ? slide : -slide,
      opacity: 0.6,
    }),
    center: { x: 0, opacity: 1 },
    exit: (direction: string) => ({
      x: direction === 'next' ? -slide : slide,
      opacity: 0.6,
    }),
  }

  const transition = {
    duration: prefersReducedMotion ? 0 : 0.3,
    ease: 'circOut',
  }

  const TOTAL_STEPS = 5
  const stepComponent = STEPS[step - 1]

  return (
    <section className="cake-maker container" id="cake-maker">
      <p className="sr-only" aria-live="polite">
        Etapa {step} de {TOTAL_STEPS}: {HEADINGS[step - 1].title}
      </p>

      <div className="cake-maker__details">
        <Stepper
          totalSteps={TOTAL_STEPS}
          currentStep={step}
          onBack={goToPreviousStep}
          onGoToStep={goToStep}
        />

        <div>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="cake-maker__details__header"
            >
              <motion.h2>{HEADINGS[step - 1].title}</motion.h2>
              <motion.p
                dangerouslySetInnerHTML={{
                  __html: HEADINGS[step - 1].description,
                }}
              />

              {step === 1 && (
                <motion.p className="cake-maker__details__header__pdf-link">
                  Ou{' '}
                  <a href="/menu.pdf" target="_blank" rel="noopener noreferrer">
                    <span>acesse o menu digital</span>
                  </a>
                  <BiLinkAlt size={24} />
                </motion.p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="cake-maker__content">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={transition}
            className="cake-maker__content__step"
          >
            {createElement(stepComponent, { orders })}
          </motion.div>
        </AnimatePresence>

        <div className="divider" />

        <div className="cake-maker__content__footer-area">
          {/* Fica no DOM enquanto a etapa estiver incompleta, para que o
              aria-describedby anuncie o motivo já ao focar o botão, e só
              aparece na tela depois que o usuário tenta avançar. */}
          {blockingMessage && (
            <p
              id="cake-maker-blocking-message"
              className={
                showBlockingMessage
                  ? 'cake-maker__content__footer__message'
                  : 'sr-only'
              }
              role={showBlockingMessage ? 'alert' : undefined}
            >
              {blockingMessage}
            </p>
          )}

          <div
            className="cake-maker__content__footer"
            data-last-step={step === TOTAL_STEPS}
          >
            <ResumeButton onClick={() => setIsResumeModalOpen(true)} />

            <Button
              text={step < TOTAL_STEPS ? 'Próximo' : 'Garantir agendamento'}
              variant="primary"
              rightRender={
                step < TOTAL_STEPS ? (
                  <BiRightArrowAlt size={24} />
                ) : (
                  <BiCalendar size={24} />
                )
              }
              onClick={() => {
                if (blockingMessage) {
                  setShowBlockingMessage(true)
                  return
                }

                if (step < TOTAL_STEPS) {
                  goToNextStep()
                } else {
                  setIsConfirmationModalOpen(true)
                }
              }}
              aria-disabled={!!blockingMessage}
              aria-describedby={
                blockingMessage ? 'cake-maker-blocking-message' : undefined
              }
              containerClassName="action-button"
            />
          </div>
        </div>
      </div>

      <ResumeModal
        isOpen={isResumeModalOpen}
        onClose={() => setIsResumeModalOpen(false)}
      />

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
      />
    </section>
  )
}
